import { APIBaseResponse } from '../api';
interface PdfLoadingState {
  [key: string]: {
    view: boolean;
    filename: boolean;
  };
}
export enum ActionType {
  VIEW = 'view',
  UNLINK = 'unlink',
  DELETE = 'delete',
  METADATA = 'metadata',
}

export enum ArticleType {
  KNOWLEDGE_ARTICLE = 'knowledge_article',
  EXPERT_ANSWER = 'expert_answer',
}

export enum ArticleStatus {
  PENDING = 'pending',
  DRAFTED = 'drafted',
  PUBLISHED = 'published',
  IN_PROGRESS = 'in_progress',
  ACTIVE = 'active',
}

interface MetricsSummary {
  total: number;
  used: number;
  units: string;
}

interface MetricSpecificData {
  quantity: number;
  size: number;
  units: string;
}

interface AdvisorMetrics {
  summary?: MetricsSummary;
  pdf?: MetricSpecificData;
  xml?: MetricSpecificData;
  csv?: MetricSpecificData;
  url?: MetricSpecificData;
  html?: MetricSpecificData;
  xlsx?: MetricSpecificData;
  xls?: MetricSpecificData;
  doc?: MetricSpecificData;
  docx?: MetricSpecificData;
}

interface Advisor {
  advisor_id: string;
  advisor_name: string;
  advisor_created: string;
  metrics: AdvisorMetrics[];
  tenant_id: string;
  bucket_name: string;
}

export interface AdvisorKnowledgeListApiResponse extends APIBaseResponse {
  data: Advisor[];
}

export interface CtyKbData {
  source_id: string;
  file_name: string;
  tenant_id: string;
  source_path: string;
  type: string;
  size: string;
  status: string;
  advisor_id: string;
  date: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  source_type?: string;
  /** Per-file metadata keys/values from knowledge API */
  metadata?: Record<string, string | number | null | undefined>;
}
interface AdvisorSpecificData {
  metrics: AdvisorMetrics[];
  cty_kb_data: CtyKbData[];
  tenant_id: string;
  bucket_name: string;
  /** When present, table/payload data is loaded from this URL (same shape as this object). */
  presigned_url?: string;
}

export interface AdvisorSpecificKnowledgeAPIResponse extends APIBaseResponse {
  data: AdvisorSpecificData;
}

/** GET /api/v2/knowledge/metadata/keys — transformed to { keys } */
export interface KnowledgeMetadataKeysResult {
  keys: string[];
}

export interface KnowledgeSorting {
  field: string;
  order: 'asc' | 'desc';
}

/** POST body + path advisor for getAdvisorKnowledgeDetails */
export interface GetAdvisorKnowledgeDetailsArg {
  advisorId: string;
  pagination: { page: number; page_size: number };
  column_filters: unknown[];
  metadata_filters: unknown[];
  sorting?: KnowledgeSorting;
}

export interface PresignedUrlResponse extends APIBaseResponse {
  data: {
    source_id: string;
    filename: string;
    parts: number;
    upload_id: string;
    presigned_urls: { [key: string]: string };
  }[];
}

export interface Metadata {
  id: string;
  name: string;
  value: string | string[];
  status?: string;
  type?: string;
}

export interface GetKnowledgeMetadataApiResponse extends APIBaseResponse {
  data: Metadata[];
}
export interface UpdateKnowledgeMetadataApiResponse extends APIBaseResponse {
  data: Metadata;
}

export interface expertAnswerKnowledgeMetadataPayload {
  status: string;
  metadata: Metadata[];
}

export interface UrlSourceListUpload {
  type: string;
  advisor_id: string;
  urls: string[];
}
export interface ActionsCellRendererProps {
  params: any;
  actions: {
    t: any;
    handleKnowledgePdfViewer: any;
    getPdfLink: any;
    isTableFileLoading: PdfLoadingState;
    setIsTableFileLoading: React.Dispatch<
      React.SetStateAction<PdfLoadingState>
    >;
    handleFileDelink: any;
    handleFileDelete: any;
    onViewMetaData: (item: any, actionType: ActionType) => void;
    handleViewOntologyTerms?: (item: any) => void;
    onOpenArticle?: (item: any) => void;
  };
  getQueryRefetch?: () => void;
}

export interface UploadKnowledgeArticlePayload {
  advisor_id: string;
  type: 'expert_answer' | 'knowledge_article';
  question: string;
  answer: string;
  status: string;
  // For expert_answer type
  sender_id?: string;
  message_id?: string;
  // For knowledge_article type
  name?: string;
  // Legacy fields for backward compatibility
  article_name?: string;
  metadata?: Metadata[];
  article_status?: string;
  article_type?: string;
  article_id?: string;
  conversation_id?: string;
}

export interface CreateExpertAnswerPayload {
  advisor_id: string;
  type: string;
  question: string;
  answer: string;
  status: string;
  metadata: Metadata[];
  sender_id: string;
  message_id: string;
}


/** Tenant-level metadata attribute schema — GET/POST/PATCH/DELETE /api/v2/knowledge/metadata/definitions */
export type MetadataDefinitionType = 'string' | 'number' | 'list';
export type MetadataDefinitionStatus = 'active' | 'inactive' | 'deleted';

export interface MetadataDefinition {
  id: string;
  name: string;
  type: MetadataDefinitionType;
  value: string[] | null;
  status: MetadataDefinitionStatus;
  sources_count: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface GetMetadataDefinitionsApiResponse extends APIBaseResponse {
  data: MetadataDefinition[];
}

export interface MetadataDefinitionMutationApiResponse extends APIBaseResponse {
  data: MetadataDefinition;
}

export interface CreateMetadataDefinitionPayload {
  name: string;
  type: MetadataDefinitionType;
  value: string[] | null;
}

export interface UpdateMetadataDefinitionPayload {
  name?: string;
  type?: MetadataDefinitionType;
  value?: string[] | null;
  status?: 'active' | 'inactive';
}

/** Domain Dictionary — POST /api/v1/dictionary/query, /create, PATCH /{id}, POST /update_knowledge */
export type DictionaryLinkStatus =
  | 'active'
  | 'inactive'
  | 'updated'
  | 'failed'
  | 'inprogress'
  | 'in_progress';

export interface DictionaryItem {
  id: string;
  advisor_id: string | null;
  domain_type: string;
  name: string;
  fullform: string;
  category: string;
  scope: Record<string, string[]>;
  synonyms: Record<string, string[]>;
  status: string;
  link_status: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string | null;
  metadata: Record<string, unknown>;
}

export interface DictionaryQueryData {
  items: DictionaryItem[];
  total_count: number;
  page: number;
  per_page: number;
  categories: string[];
  scope_models: string[];
  link_statuses?: string[];
}

export interface DictionaryQueryApiResponse extends APIBaseResponse {
  data: DictionaryQueryData;
}

export interface DictionaryPagination {
  page: number;
  per_page: number;
}

export interface DictionaryQueryPayload {
  pagination?: DictionaryPagination;
  filters?: Record<string, unknown>[];
}

export interface UpdateDictionaryPayload {
  name?: string;
  fullform?: string;
  category?: string;
  scope?: Record<string, string[]>;
  synonyms?: Record<string, string[]>;
}

export interface CreateDictionaryPayload {
  name: string;
  domain_type: string;
  fullform: string;
  category?: string;
  scope: Record<string, string[]>;
  synonyms: Record<string, string[]>;
  metadata: Record<string, unknown>;
}

export interface DictionaryMutationApiResponse extends APIBaseResponse {
  data: {
    dictionary: DictionaryItem;
  };
}

export interface DictionaryKnowledgeSelectedId {
  id: string;
  domain_type: string;
}

export interface UpdateDictionaryKnowledgePayload {
  mode: 'selected' | 'all';
  /** Sent for mode 'all' — restricts the knowledge update to the active tab's domain type. */
  domain_type?: string;
  /** Sent for mode 'selected' — each id carries its own domain type. */
  ids?: DictionaryKnowledgeSelectedId[];
}

/** Item in the POST /presignedurl/generate request body used for dictionary bulk upload. */
export interface DictionaryPresignedUrlItem {
  filename: string;
  source_type: string;
  metadata: { domain_type: string };
}

export interface DictionaryPresignedUrlData {
  source_id: string;
  file_name: string;
  source_type: string;
  content_type: string;
  s3_key: string;
  presigned_url: string;
}

export interface DictionaryPresignedUrlResponse extends APIBaseResponse {
  data: DictionaryPresignedUrlData[];
}

export interface DictionaryBulkUploadPayload {
  s3_path: string;
  domain_type: string;
}

export interface DictionaryTemplateUrlResponse extends APIBaseResponse {
  data: {
    s3_key: string;
    presigned_url: string;
  };
}

export interface ExpertAnswerKnowledgeArticle extends UploadKnowledgeArticlePayload {
  type: 'expert_answer';
  sender_id: string;
  message_id: string;
  conversation_id: string;
  original_ai_response?: string;
  expert_answer_id?: string;
}
